// The Country row on the admin talent card. Two facts only, never IP or
// location: the country the talent picked for payouts (User.bankCountry, a
// display name like "United States") and, for MassPay talent, the country
// that issued the ID MassPay's identity check read (masspay.kycDocCountry,
// ISO-2). The ID country is red when it is not the payout country, the same
// rule as the Approvals board (AdminApprovalsTab).
import { FALLBACK_COUNTRIES } from "../../lib/supportedCountries";
import { DjUser } from "./types";

const ISO2 = /^[A-Za-z]{2}$/;

// "ID" → "Indonesia" (English: the founder reads the admin in English).
export function regionName(code: string): string {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code.toUpperCase()) || code;
  } catch {
    return code;
  }
}

let intlNames: Map<string, string> | null = null;
// Every region name the browser knows → its code, built once on first use.
// Catches a listed country that isn't in the offline mirror (the server lists
// 194 countries; the mirror only the 46 open or soon-open ones).
function intlLookup(name: string): string | null {
  if (!intlNames) {
    intlNames = new Map();
    const A = 65;
    for (let i = 0; i < 26; i++) {
      for (let j = 0; j < 26; j++) {
        const code = String.fromCharCode(A + i, A + j);
        const n = regionName(code);
        if (n && n !== code) intlNames.set(n.toLowerCase(), code);
      }
    }
  }
  return intlNames.get(name.toLowerCase()) || null;
}

/** Payout country name → ISO-2 ("United States" → "US"); null when unknown. */
export function countryCode(name?: string | null): string | null {
  const n = (name || "").trim();
  if (!n) return null;
  const listed = FALLBACK_COUNTRIES.find((c) => c.name.toLowerCase() === n.toLowerCase());
  if (listed) return listed.code;
  if (ISO2.test(n)) return n.toUpperCase();
  return intlLookup(n);
}

export interface CountryRow {
  /** ISO-2 for the flag, or null (name we can't map: no flag, name still shown). */
  code: string | null;
  name: string;
  /** MassPay talent with an ID decision: where the ID is from. */
  idFrom: { name: string; mismatch: boolean } | null;
}

/** null when the talent hasn't picked a payout country: the row is hidden. */
export function countryRow(dj: Pick<DjUser, "bankCountry" | "masspay">): CountryRow | null {
  const name = (dj.bankCountry || "").trim();
  if (!name) return null;
  const code = countryCode(name);
  const doc = (dj.masspay?.kycDocCountry || "").trim().toUpperCase();
  const idFrom = ISO2.test(doc)
    ? { name: regionName(doc), mismatch: !!code && doc !== code }
    : null;
  return { code, name: code && ISO2.test(name) ? regionName(code) : name, idFrom };
}
